/**
 * Types mirroring the FastAPI schemas.
 *
 * The backend serialises every field as camelCase (one alias generator on its
 * base model), so these are a direct transcription with no mapping layer.
 */

// -- enums ------------------------------------------------------------------

export const JUDGMENT_STATUSES = [
  'open',
  'closed',
  'amended',
  'revived',
  'voided',
  'unknown',
] as const
export type JudgmentStatus = (typeof JUDGMENT_STATUSES)[number]

export const PARTY_ROLES = ['debtor', 'creditor', 'other'] as const
export type PartyRole = (typeof PARTY_ROLES)[number]

export const SORT_FIELDS = [
  'entered_on',
  'judgment_number',
  'docket_number',
  'amount_value',
  'status',
  'filing_location',
  'matched_name',
  'case_title',
] as const
export type SortField = (typeof SORT_FIELDS)[number]
export type SortDirection = 'asc' | 'desc'

export const JOB_TYPES = ['curl_capture', 'browser_search', 'json_import'] as const
export type JobType = (typeof JOB_TYPES)[number]

export const JOB_STATUSES = [
  'pending',
  'running',
  'succeeded',
  'partial',
  'failed',
] as const
export type JobStatus = (typeof JOB_STATUSES)[number]

// -- judgments --------------------------------------------------------------

export interface Party {
  id: number
  name: string
  role: PartyRole
  roleText: string
  statusText: string
  debtSequence: string
}

export interface CaptureSummary {
  id: number
  label: string
  sourceType: JobType
  isCapped: boolean
  isComplete: boolean
  /** The portal ran the search and holds no matching records. */
  noRecords: boolean
  recordsFound: number
  reportedTotal: number
}

export interface Judgment {
  id: number
  judgmentNumber: string
  docketNumber: string
  dateEntered: string
  enteredOn: string | null
  debtorNames: string[]
  creditorNames: string[]
  amountText: string
  amountValue: string | null
  status: JudgmentStatus
  statusText: string
  caseTitle: string
  filingLocation: string
  matchedName: string
  matchedPartyRole: PartyRole
  detailFetched: boolean
}

export interface JudgmentDetail extends Judgment {
  detailFields: Record<string, string>
  parties: Party[]
  captures: CaptureSummary[]
  createdAt: string
  updatedAt: string
}

// -- paging -----------------------------------------------------------------

export interface PageMeta {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

export interface Page<T> {
  items: T[]
  meta: PageMeta
}

// -- facets & stats ---------------------------------------------------------

export interface FacetValue {
  value: string
  label: string
  count: number
}

export interface JudgmentFacets {
  statuses: FacetValue[]
  filingLocations: FacetValue[]
  partyRoles: FacetValue[]
}

export interface DatasetStats {
  totalJudgments: number
  totalParties: number
  totalCaptures: number
  detailedJudgments: number
  cappedCaptures: number
  distinctNames: number
  earliestYear: number | null
  latestYear: number | null
  totalAmount: string | null
  byStatus: FacetValue[]
  byYear: FacetValue[]
  byLocation: FacetValue[]
}

// -- jobs & captures --------------------------------------------------------

export interface Job {
  id: number
  jobType: JobType
  status: JobStatus
  label: string
  parameters: Record<string, unknown>
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  durationSeconds: number | null
  recordsIngested: number
  recordsCreated: number
  recordsMerged: number
  requestsAllowed: number
  requestsBlocked: number
  error: string
  messages: string[]

  /**
   * The split tree. A search that hit the portal's 300-record display cap is
   * narrowed into parts, each its own job; without these a flat list shows
   * several rows named after one surname with no visible relationship.
   */
  parentJobId: number | null
  /** The submitted search at the top of this job's tree. */
  rootJobId: number | null
  /** How many splits deep. 0 for a search someone submitted. */
  splitDepth: number
}

/**
 * How far a whole search has got once it was split into parts.
 *
 * Returned only when a tree exists. The submitted row's own status describes
 * just its own first 300 records, so on its own it reads "complete" while most
 * of the search is still queued.
 */
export interface SplitProgress {
  total: number
  pending: number
  running: number
  succeeded: number
  partial: number
  failed: number
  recordsIngested: number
}

export interface JobDetail extends Job {
  detailProgress: DetailProgress | null
  splitProgress: SplitProgress | null
}

export interface DetailProgress {
  pending: number
  running: number
  succeeded: number
  failed: number
  expired: number
  detailsRead: number
}

export interface JobCreated {
  job: Job
  message: string
}

export interface Capture {
  id: number
  label: string
  sourceType: JobType
  lastName: string
  firstName: string
  recordsFound: number
  reportedTotal: number
  pagesRead: number
  /** Pages the portal said the search had. 0 when unknown (an import). */
  pagesAvailable: number
  requestsMade: number
  detailsFetched: number
  isCapped: boolean
  isComplete: boolean
  /** The portal ran the search and holds no matching records. */
  noRecords: boolean
  verdict: string
  notes: string[]
  createdAt: string
}

export interface ImportableFile {
  path: string
  name: string
  sizeBytes: number
  modifiedAt: string
  recordCount: number | null
}

// -- requests ---------------------------------------------------------------

export interface JsonImportRequest {
  paths: string[]
}

export interface CurlCaptureRequest {
  curlCommand: string
  label?: string
  fetchDetails: boolean
  maxPages: number
}

export interface BrowserSearchRequest {
  lastName: string
  firstName?: string
  middleName?: string
  status?: string
  partyRole?: string
  location?: string
  startDate?: string
  endDate?: string
  fetchDetails: boolean
  maxPages: number
}

// -- filters ----------------------------------------------------------------

/** Client-side filter state; serialised straight onto the query string. */
export interface JudgmentFilters {
  q: string
  status: JudgmentStatus[]
  filingLocation: string[]
  partyRole: PartyRole | null
  dateFrom: string
  dateTo: string
  amountMin: string
  amountMax: string
  hasDetails: boolean | null
  captureId: number | null
}

export const EMPTY_FILTERS: JudgmentFilters = {
  q: '',
  status: [],
  filingLocation: [],
  partyRole: null,
  dateFrom: '',
  dateTo: '',
  amountMin: '',
  amountMax: '',
  hasDetails: null,
  captureId: null,
}

export interface ApiErrorPayload {
  code: string
  message: string
  detail?: string
}

/** Which ingestion routes this build of the extraction engine supports. */
export interface JobCapabilities {
  jsonImport: boolean
  browserSearch: boolean
  /**
   * Absent on builds whose engine has no cURL replay at all. Optional rather
   * than required so a missing flag reads as "unavailable" instead of
   * type-checking against a field the API never sends.
   */
  curlCapture?: boolean
}

/** What the server found in an uploaded file, before anything is stored. */
export interface ImportPreview {
  detectedFormat: string
  totalRows: number
  usableRows: number
  skippedRows: number
  /** Original header -> canonical field it was matched to. */
  mappedColumns: Record<string, string>
  unrecognisedColumns: string[]
  missingColumns: string[]
  rowErrors: string[]
}

export interface FileImportResult {
  preview: ImportPreview
  job: Job | null
  message: string
}

/** The columns an uploaded file may contain. */
export interface ImportSchema {
  identifyingColumns: string[]
  columns: Record<string, string[]>
  formats: string[]
  maxBytes: number
  listSeparators: string[]
}
